
import React, { useEffect, useState } from "react";
import ShowToast from "../Common/Toast";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useVendorStore from "../HomePage/useVendorStore";
import NoEmailBottomSheet from "./NoEmailBottomSheet";
import { Loader } from "../Common/Loader";
import {
  Button,
  Details,
  FlexColumn,
  WhiteWrapper,
} from "../Common/styles.common";
import {
  GrnPageWrapper,
  GrnSummaryCard,
  GrnSummaryMetric,
  GrnSummaryMetrics,
  GrnSummarySectionWrapper,
  ItemCard,
  ProductThumb,
  DisputeRowGrid,
  DisputeValueCard,
  DisputeInputCard,
  DisputeCardLabel,
  DisputeCardRow,
  DisputeCardValue,
  DisputeCardUnit,
  DisputeNativeInput,
  DisputeDescriptionWrapper,
  DisputeDescriptionTextarea,
  DisputeErrorIcon,
  DisputeItemError,
  DisputeDescriptionError,
  GrnInfoLabelWrapper,
  DisputeInfoLabel,
} from "./styles.grn";
import BackButton from "../Button/BackButton";
import GrnDetailsSummaryTable from "./GrnDetailsSummary/GrnDetailsSummaryTable";
import Spinner from "sushiweb/Spinner/Spinner";
import { GRN_DISPUTE_ISSUE_TYPE, STATUS_BEGIN, STATUS_DONE, UPDATE_SELLER_STATUS_ENTITY_TYPE } from "utils/constants/others";
import { strings } from "utils/constants/strings";

const RaiseDisputePage = () => {
  const { source, purchaseOrderId, grnId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const getPoGrnMappings = useVendorStore((state) => state.getPoGrnMappings);
  const getPoGrnMappingStatus = useVendorStore((state) => state.getPoGrnMappingStatus);
  const poGrnList = useVendorStore((state) => state.poGrnList);
  const getSellerProfile = useVendorStore((state) => state.getSellerProfile);
  const setPendingDisputePayload = useVendorStore((state) => state.setPendingDisputePayload);
  const [grn, setGrn] = useState(null);
  const [grnItems, setGrnItems] = useState([]);
  const [showLoader, setShowLoader] = useState(false);

  // Dispute form state — initialised from restored state when returning from email verification
  const [disputeIssueDescription, setDisputeIssueDescription] = useState(
    location.state?.restoredDisputeDescription ?? ""
  );
  const [disputeItemIssues, setDisputeItemIssues] = useState(
    location.state?.restoredDisputeItemIssues ?? {}
  );
  const [submitDisputeLoading, setSubmitDisputeLoading] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [descriptionError, setDescriptionError] = useState("");
  const [itemErrors, setItemErrors] = useState({});

  // Email verification flow
  const [showNoEmailSheet, setShowNoEmailSheet] = useState(false);

  // Clear restored state from browser history so a refresh doesn't re-apply it
  useEffect(() => {
    if (location.state?.restoredDisputeDescription !== undefined ||
      location.state?.restoredDisputeItemIssues !== undefined) {
      window.history.replaceState({}, "");
    }
  }, []);

  useEffect(() => {
    getPoGrnMappings(
      {
        po_grn_mapping_id: grnId,
        is_po_grn_items_required: true,
        get_cn_data: true,
        fetch_seller_status_meta_data: true,
        fetch_ledger_data: true,
        get_prn_data: true,
        is_payment_details_required: true,
        skip_total_page_count: true,
        fetch_vendor_dispute_data: true,
      },
      true
    );
  }, []);

  useEffect(() => {
    switch (getPoGrnMappingStatus) {
      case STATUS_BEGIN:
        setShowLoader(true);
        break;
      case STATUS_DONE:
        setShowLoader(false);
        break;
    }
  }, [getPoGrnMappingStatus]);

  useEffect(() => {
    if (getPoGrnMappingStatus === STATUS_DONE && poGrnList) {
      setGrn(poGrnList[0]);
    }
  }, [poGrnList]);

  useEffect(() => {
    setGrnItems(Array.isArray(grn?.po_grn_items) ? grn.po_grn_items : []);
  }, [grn]);

  const grnSummary = grn?.grn_page_data?.summary;

  const handleClaimChange = (itemId, field) => (val) => {
    setDisputeItemIssues((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: val,
      },
    }));
    // Clear per-item error when user edits a claim for that item
    setItemErrors((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  // Returns { valid: true, issues } on success, or { valid: false } after setting
  // the appropriate error state when any validation rule is violated.
  const validateHandleSubmitDispute = () => {
    if (!disputeIssueDescription || disputeIssueDescription.trim().length === 0) {
      setDescriptionError("Please provide a dispute description before submitting.");
      return { valid: false };
    }
    setDescriptionError("");

    const issues = Object.entries(disputeItemIssues).reduce(
      (acc, [grnItemId, v]) => {
        if (v.claimedGrnQty) {
          acc.push({
            grn_item_id: Number(grnItemId),
            issue_type: GRN_DISPUTE_ISSUE_TYPE.GRN_QUANTITY,
            claimed_value: String(v.claimedGrnQty),
          });
        }
        if (v.claimedGstRate) {
          acc.push({
            grn_item_id: Number(grnItemId),
            issue_type: GRN_DISPUTE_ISSUE_TYPE.GST_PERCENTAGE,
            claimed_value: String(v.claimedGstRate),
          });
        }
        if (v.claimedGrnPrice) {
          acc.push({
            grn_item_id: Number(grnItemId),
            issue_type: GRN_DISPUTE_ISSUE_TYPE.PRICE_PER_UNIT,
            claimed_value: String(v.claimedGrnPrice),
          });
        }
        return acc;
      },
      []
    );

    if (issues.length === 0) {
      setDescriptionError("Please enter at least one claimed value before submitting.");
      return { valid: false };
    }

    const newItemErrors = {};
    for (const issue of issues) {
      const val = Number(issue.claimed_value);
      if (val <= 0) {
        newItemErrors[issue.grn_item_id] = "Claimed value must be greater than 0.";
      } else if (issue.issue_type === GRN_DISPUTE_ISSUE_TYPE.GRN_QUANTITY) {
        const grnItem = grnItems.find((i) => i.id === issue.grn_item_id);
        if (grnItem && val === Number(grnItem.grn_quantity)) {
          newItemErrors[issue.grn_item_id] = strings.grnQtyDisputeEqualActual;
        }
      } else if (issue.issue_type === GRN_DISPUTE_ISSUE_TYPE.GST_PERCENTAGE) {
        const grnItem = grnItems.find((i) => i.id === issue.grn_item_id);
        if (grnItem && val === Number(grnItem.gst_rate)) {
          newItemErrors[issue.grn_item_id] = strings.gstRateDisputeEqualActual;
        } else if (val >= 50) {
          newItemErrors[issue.grn_item_id] = "GST percentage claim must be less than 50.";
        }
      } else if (issue.issue_type === GRN_DISPUTE_ISSUE_TYPE.PRICE_PER_UNIT) {
        const grnItem = grnItems.find((i) => i.id === issue.grn_item_id);
        if (grnItem) {
          if (val === grnItem.grn_price_per_unit) {
            newItemErrors[issue.grn_item_id] = strings.priceDisputeEqualActual;
          } 
        }
      }
    }
    if (Object.keys(newItemErrors).length > 0) {
      setItemErrors(newItemErrors);
      return { valid: false };
    }
    setItemErrors({});
    return { valid: true, issues };
  };

  const handleSubmitDispute = async () => {
    const validation = validateHandleSubmitDispute();
    if (!validation.valid) return;
    const { issues } = validation;

    const grnNumber = grn?.grn_number;
    if (!grnNumber) return;

    const payload = {
      entity_id: Number(grnId),
      entity_type: UPDATE_SELLER_STATUS_ENTITY_TYPE.PO_GRN_MAPPING,
      seller_status: "IN_DISPUTE",
      account_id: Number(window.localStorage.getItem("outlet_id")),
      grn_dispute: {
        issue_description: disputeIssueDescription,
        issues,
      },
    };

    setSubmitDisputeLoading(true);

    // ── Email check: fetch the latest seller profile and inspect email ──
    // We call getSellerProfile() which updates the Zustand store, then read
    // the current value directly from getState() to avoid stale closures.
    try {
      await getSellerProfile();
    } catch (error) {
      setSubmitDisputeLoading(false);
      setErrorMsg(error?.response?.data?.error?.message || "Something went wrong. Please try again.");
      setShowToast(true);
      return;
    }

    // Read fresh state directly via getState() to avoid React stale-closure issue.
    const currentEmail = useVendorStore.getState().sellerProfile?.user_email ?? "";

    if (currentEmail === "") {
      // Email is missing — pause submission, show bottom sheet.
      setSubmitDisputeLoading(false);
      setShowNoEmailSheet(true);
      return;
    }

    // Email present — hand the dispute payload to the store (single source of
    // truth also read by Invoice.jsx) and let the Invoice page's existing
    // upload + confirm flow handle document upload and updateSellerStatus.
    setSubmitDisputeLoading(false);
    setPendingDisputePayload(payload);
    navigate(invoicePath);
  };

  const invoicePath =
    source === "all"
      ? `/grn/invoice/all/${purchaseOrderId}/${grnId}`
      : `/grn/invoice/pending/${purchaseOrderId}/${grnId}`;

  const backPath =
    source === "all"
      ? `/grn/details/all/${purchaseOrderId}/${grnId}`
      : `/grn/details/list/${purchaseOrderId}/${grnId}`;

  const disputeAllowedMap =
    grn?.vendor_grn_dispute_details?.vendor_grn_dispute_allowed
      ?.item_dispute_allowed_map || {};

  const disputeableItems = grnItems.filter((item) => {
    const allowed = disputeAllowedMap[item.id];
    return (
      allowed &&
      Array.isArray(allowed.allowed_issue_types) &&
      allowed.allowed_issue_types.length > 0
    );
  });

  const showPriceDisputeNotAllowedBanner = grnItems.some((item) => {
    const allowed = disputeAllowedMap[item.id];
    return (
      allowed &&
      Array.isArray(allowed.allowed_issue_types) &&
      !allowed.allowed_issue_types.includes(GRN_DISPUTE_ISSUE_TYPE.PRICE_PER_UNIT)
    );
  });

  return (
    <React.Fragment>
      {showLoader ? (
        <Loader />
      ) : (
        <GrnPageWrapper className="d-flex flex-column">
          {/* Scrollable body */}
          <div className="d-flex flex-column y-scroll" style={{ flex: 1 }}>
            {/* Header — back button + title (no header tag strip) */}
            <Details $flatBottom>
              <div className="d-flex align-items-center ml-4">
                <BackButton pathName={backPath} />
                <div className="d-flex flex-column ml-1 mr-4">
                  <span className="fs-18 fw-700">Raise a dispute</span>
                  {grn?.grn_number && (
                    <span className="fs-12 fw-400 text-gray-cool mt-1">
                      {grn.grn_number}
                    </span>
                  )}
                </div>
              </div>
            </Details>

            {/* GRN Summary */}
            {grnSummary && (
              <GrnSummarySectionWrapper style={{ marginTop: "16px" }}>
                <GrnSummaryCard>
                  <GrnSummaryMetrics>
                    <GrnSummaryMetric>
                      <span className="fs-18 fw-600">
                        {grnSummary?.invoice_amount?.value}
                      </span>
                      <span className="fs-13 fw-400 text-gray-cool">
                        {grnSummary?.invoice_amount?.label}
                      </span>
                    </GrnSummaryMetric>
                    <GrnSummaryMetric>
                      <span className="fs-18 fw-600">
                        {grnSummary?.grn_amount?.value}
                      </span>
                      <span className="fs-13 fw-400 text-gray-cool">
                        {grnSummary?.grn_amount?.label}
                      </span>
                    </GrnSummaryMetric>
                  </GrnSummaryMetrics>
                  <GrnDetailsSummaryTable rows={grnSummary?.rows} />
                </GrnSummaryCard>
              </GrnSummarySectionWrapper>
            )}

            {/* "Raise a dispute" heading */}
            <div style={{ padding: "16px 16px 0 16px" }}>
              <span
                style={{
                  fontWeight: 700,
                  fontSize: "20px",
                  lineHeight: "26px",
                  letterSpacing: "-0.01em",
                  color: "#293142",
                }}
              >
                Raise a dispute
              </span>
            </div>

            {/* Price dispute info banner */}
            {showPriceDisputeNotAllowedBanner && (
            <GrnInfoLabelWrapper>
              <DisputeInfoLabel
                $bg_color="var(--color-base-orange-100, rgba(255, 241, 230, 1))"
                style={{
                  gap: "12px",
                  borderRadius: "12px",
                  border: "2px solid var(--color-base-white, rgba(255, 255, 255, 1))",
                  padding: "12px",
                }}
              >
                <img
                  src="/logos/exclamation-line.svg"
                  height="20px"
                  width="20px"
                  style={{ flexShrink: 0 }}
                />
                <span
                  style={{
                    fontFamily: "Our Lexend, sans-serif",
                    fontWeight: "400",
                    fontSize: "12px",
                    lineHeight: "14px",
                    letterSpacing: "0%",
                    verticalAlign: "middle",
                    color: "var(--color-base-grey-800, rgba(65, 74, 93, 1))",
                  }}
                >
                  {strings.priceDisputeNotAllowed}
                </span>
              </DisputeInfoLabel>
            </GrnInfoLabelWrapper>
            )}

            {/* Item-wise dispute input cards */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "16px 16px 16px 16px",
              }}
            >
              {disputeableItems.map((item) => {
                const allowed = disputeAllowedMap[item.id];
                const current = disputeItemIssues[item.id] || {};

                return (
                  <ItemCard key={item.id}>
                    {/* Product header */}
                    <div className="d-flex align-items-center">
                      <ProductThumb
                        src={item.product_image}
                        alt={item.product_name}
                      />
                      <FlexColumn className="ml-3" style={{ flex: 1, minWidth: 0 }}>
                        <span className="fs-16 fw-600">{item.product_name}</span>
                        <span className="fs-14 fw-400 text-gray-cool mt-1">
                          &#8377;{item.po_item_price_per_unit}/
                          {item.buy_measurement_unit}
                        </span>
                      </FlexColumn>
                    </div>

                    {/* GRN quantity dispute row */}
                    {allowed.allowed_issue_types.includes(
                      GRN_DISPUTE_ISSUE_TYPE.GRN_QUANTITY
                    ) && (
                        <DisputeRowGrid>
                          <DisputeValueCard>
                            <DisputeCardLabel>GRN quantity</DisputeCardLabel>
                            <DisputeCardRow>
                              <DisputeCardValue>{item.grn_quantity}</DisputeCardValue>
                              <DisputeCardUnit>
                                {item.buy_measurement_unit}
                              </DisputeCardUnit>
                            </DisputeCardRow>
                          </DisputeValueCard>
                          <DisputeInputCard>
                            <DisputeCardLabel>Your claim</DisputeCardLabel>
                            <DisputeCardRow>
                              <DisputeNativeInput
                                type="number"
                                placeholder="0"
                                value={current.claimedGrnQty || ""}
                                onChange={(e) =>
                                  handleClaimChange(
                                    item.id,
                                    "claimedGrnQty"
                                  )(e.target.value)
                                }
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                step="0.01"
                              />
                              <DisputeCardUnit>
                                {item.buy_measurement_unit}
                              </DisputeCardUnit>
                            </DisputeCardRow>
                          </DisputeInputCard>
                        </DisputeRowGrid>
                      )}

                    {/* PO GST % dispute row */}
                    {allowed.allowed_issue_types.includes(
                      GRN_DISPUTE_ISSUE_TYPE.GST_PERCENTAGE
                    ) && (
                        <DisputeRowGrid>
                          <DisputeValueCard>
                            <DisputeCardLabel>PO GST %</DisputeCardLabel>
                            <DisputeCardRow>
                              <DisputeCardValue>{item.gst_rate}</DisputeCardValue>
                              <DisputeCardUnit>%</DisputeCardUnit>
                            </DisputeCardRow>
                          </DisputeValueCard>
                          <DisputeInputCard>
                            <DisputeCardLabel>Your claim</DisputeCardLabel>
                            <DisputeCardRow>
                              <DisputeNativeInput
                                type="number"
                                placeholder="0"
                                value={current.claimedGstRate || ""}
                                onChange={(e) =>
                                  handleClaimChange(
                                    item.id,
                                    "claimedGstRate"
                                  )(e.target.value)
                                }
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                step="0.01"
                              />
                              <DisputeCardUnit>%</DisputeCardUnit>
                            </DisputeCardRow>
                          </DisputeInputCard>
                        </DisputeRowGrid>
                      )}

                    {/* GRN price per unit dispute row */}
                    {allowed.allowed_issue_types.includes(
                      GRN_DISPUTE_ISSUE_TYPE.PRICE_PER_UNIT
                    ) && (
                        <DisputeRowGrid>
                          <DisputeValueCard>
                            <DisputeCardLabel>GRN price per unit</DisputeCardLabel>
                            <DisputeCardRow>
                              <DisputeCardValue>
                                {item.grn_price_per_unit}
                              </DisputeCardValue>
                              <DisputeCardUnit>{`₹/${item?.buy_measurement_unit}`}</DisputeCardUnit>
                            </DisputeCardRow>
                          </DisputeValueCard>
                          <DisputeInputCard>
                            <DisputeCardLabel>Your claim</DisputeCardLabel>
                            <DisputeCardRow>
                              <DisputeNativeInput
                                type="number"
                                placeholder="0"
                                value={current.claimedGrnPrice || ""}
                                onChange={(e) =>
                                  handleClaimChange(
                                    item.id,
                                    "claimedGrnPrice"
                                  )(e.target.value)
                                }
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                step="0.01"
                              />
                              <DisputeCardUnit>{`₹/${item?.buy_measurement_unit}`}</DisputeCardUnit>
                            </DisputeCardRow>
                          </DisputeInputCard>
                        </DisputeRowGrid>
                      )}
                    {itemErrors[item.id] && (
                      <DisputeItemError>
                        <DisputeErrorIcon src="/logos/exclamation-line.svg" />
                        <span>{itemErrors[item.id]}</span>
                      </DisputeItemError>
                    )}
                  </ItemCard>
                );
              })}
            </div>

            <div style={{ padding: "0 16px 16px 16px" }}>
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e6e9ef",
                  borderRadius: "16px",
                  padding: "16px",
                  boxShadow: "0px 1px 4px rgba(28, 28, 28, 0.06)",
                }}
              >
                <DisputeDescriptionWrapper style={{ marginTop: 0 }}>
                  <span className="fs-16 fw-700 text-dark">
                    Share details about the dispute
                  </span>
                  <DisputeDescriptionTextarea
                    placeholder="Type dispute details.."
                    value={disputeIssueDescription}
                    onChange={(e) => {
                      setDisputeIssueDescription(e.target.value);
                      if (descriptionError) setDescriptionError("");
                    }}
                    rows={5}
                  />
                </DisputeDescriptionWrapper>
              </div>
                  {descriptionError && (
                    <DisputeDescriptionError>
                      <DisputeErrorIcon src="/logos/exclamation-line.svg" />
                      <span>{descriptionError}</span>
                    </DisputeDescriptionError>
                  )}
            </div>
          </div>

          {/* Sticky bottom CTA */}
          <WhiteWrapper className="px-5">
            <Button
              className="fs-13 text-white fw-600 mb-3"
              onClick={handleSubmitDispute}
              disabled={submitDisputeLoading}
            >
              {submitDisputeLoading ? (
                <Spinner color="white" />
              ) : (
                "Submit Dispute"
              )}
            </Button>
          </WhiteWrapper>
          {showNoEmailSheet && (
            <NoEmailBottomSheet
              isOpen={showNoEmailSheet}
              onClose={() => setShowNoEmailSheet(false)}
              source={source}
              purchaseOrderId={purchaseOrderId}
              grnId={grnId}
              formState={{ disputeIssueDescription, disputeItemIssues }}
            />
          )}
        </GrnPageWrapper>
      )}
      {showToast && errorMsg && (
        <ShowToast
          errorMsg={errorMsg}
          type="error"
          onClose={() => setShowToast(false)}
        />
      )}
    </React.Fragment>
  );
};

export default RaiseDisputePage;
